import { Controller } from "@hotwired/stimulus"
import { createSelect2Manager } from "../lib/select2_manager"

// A fixed_amount_per_lead discount only makes sense on fresh leads, so the
// Bucket (lead_ages) field is pinned to "fresh" and locked for that type —
// unless its optional "Aged % Off" is set, which also covers aged orders.
const FRESH_BUCKET = "fresh"
const PER_LEAD_TYPE = "fixed_amount_per_lead"
const TIERED_PERCENTAGE_TYPE = "tiered_percentage"
const FIXED_AMOUNT_TYPE = "fixed_amount"
const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })

export default class extends Controller {
  static targets = [
    "discountType",
    "discountValue",
    "tieredRulesRow",
    "tieredRule",
    "voucherBalance",
    "limitTotalRedemptions",
    "limitPerCustomer",
    "limitsSummary",
    "maxRedemptionsField",
    "maxTotalRedemptions",
    "startDate",
    "endDate",
    "filterSelect",
    "leadAgesSelect",
    "leadAgesHint",
    "perLeadAgedRow",
    "perLeadAged",
    "statesSelect",
    "disableRestrictions",
    "freeOrder",
    "freeOrderRow",
    "freeOrderCouponHint",
    "freeOrderVoucherHint",
    "fixedAmountHint"
  ]

  static values = {
    defaultDurationHours: { type: Number, default: 72 },
    voucherDurationHours: { type: Number, default: 8760 },
    editMode: { type: Boolean, default: false },
    // CouponCode::UNLIMITED_BY_DEFAULT_DISCOUNT_TYPES — discount types whose new
    // form starts with both redemption limits unchecked (#14875).
    unlimitedDefaultTypes: { type: Array, default: [] }
  }

  connect() {
    this.updateDiscountValueConstraints()
    this.updateTieredRulesVisibility()
    this.updateFreeOrderToggle()
    this.toggleMaxRedemptions()
    this.updateLimitsSummary()
    this.updateFixedAmountHint()
    // On edit forms, treat the server-rendered ends_at as already user-provided
    // so it isn't silently overwritten when starts_at or discount_type changes.
    // On new forms the server pre-seeds a default ends_at that MUST remain
    // reactive to discount-type / starts-at changes.
    this.endDateManuallyModified = this.editModeValue && this.hasEndDateTarget && !!this.endDateTarget.value
    // The limit defaults only ever steer a blank new form. An edit form carries
    // the saved limits, so they count as already chosen from the start.
    this.limitsManuallyModified = this.editModeValue
    // Same for Disable Restrictions: the server renders the new-form default
    // (ON), so connect never re-steers it — a 422 re-render keeps the admin's
    // submitted choice.
    this.disableRestrictionsManuallyModified = this.editModeValue

    // On phones/tablets fall back to the native multi-select: Select2's styled
    // box does not open on tap on iOS inside this modal, leaving the product
    // filters dead (the native <select>s on the same form work fine). Desktop
    // keeps the richer Select2 widget.
    this.select2Manager = createSelect2Manager({ nativeOnTouch: true })
    this.initializeFilterSelects()

    // Apply the per-lead Bucket lock AFTER Select2 is initialized so the
    // disabled/selected state is reflected in the Select2 widget. The lock
    // fires a Bucket change; `connecting` keeps that from re-steering Disable
    // Restrictions over the server-rendered value.
    this.connecting = true
    this.updateLeadAgesLock()
    this.connecting = false
  }

  disconnect() {
    this.select2Manager?.destroyAll()
    this.select2Manager = null
  }

  initializeFilterSelects() {
    if (!this.hasFilterSelectTarget || !this.select2Manager) return

    // When the form is hosted inside a modal (the React FormModal renders
    // Bootstrap `.modal` markup), Select2 must anchor its dropdown to the modal
    // via `dropdownParent` — otherwise it appends the dropdown to <body>, which
    // sits behind the modal's stacking context, hiding the options. On the
    // standalone /new page there is no `.modal` ancestor, so this is undefined
    // and Select2 keeps its default body placement. Unlike the modal in
    // affiliate-edit, the Turbo Frame injects this form only after the modal is
    // already shown, so there is no `shown.bs.modal` to wait on.
    const modalEl = this.element.closest(".modal")

    this.filterSelectTargets.forEach((element) => {
      this.select2Manager.init(element, () => ({
        placeholder: element.dataset.select2Placeholder || "Select options (leave empty to match all)",
        allowClear: true,
        closeOnSelect: false,
        multiple: true,
        width: "100%",
        dropdownParent: modalEl ? window.$(modalEl) : undefined
      }), this.select2Callbacks(element))
    })
  }

  // Select2 announces a selection through jQuery, which never reaches a
  // Stimulus `data-action`, so the Bucket and State filters re-apply the
  // Disable Restrictions default from a jQuery handler instead.
  select2Callbacks(element) {
    const steersRestrictions = (this.hasLeadAgesSelectTarget && element === this.leadAgesSelectTarget) ||
      (this.hasStatesSelectTarget && element === this.statesSelectTarget)
    if (!steersRestrictions) return {}

    return {
      onInitialized: (_element, $element) => {
        const handler = () => this.applyDisableRestrictionsDefault()
        $element.on("change", handler)
        return () => $element.off("change", handler)
      }
    }
  }

  updateDiscountValueConstraints() {
    if (!this.hasDiscountTypeTarget) return

    const type = this.discountTypeTarget.value
    const isPercentage = type === "percentage"
    const isVoucher = type === "voucher"
    const isTieredPercentage = type === TIERED_PERCENTAGE_TYPE

    // Disable/enable Discount Value
    if (this.hasDiscountValueTarget) {
      this.discountValueTarget.disabled = isVoucher || isTieredPercentage
      this.discountValueTarget.required = !isVoucher && !isTieredPercentage
      if (isVoucher || isTieredPercentage) {
        this.discountValueTarget.value = "0"
      } else {
        if (this.discountValueTarget.value === "0") this.discountValueTarget.value = ""
        this.discountValueTarget.min = isPercentage ? 5 : 0.01
        this.discountValueTarget.max = isPercentage ? 100 : ""
        this.discountValueTarget.step = isPercentage ? 1 : 0.01
        if (isPercentage) {
          this.discountValueTarget.placeholder = "Enter percentage (5-100)"
        } else if (type === "fixed_amount_per_lead") {
          this.discountValueTarget.placeholder = "Enter amount per lead"
        } else {
          this.discountValueTarget.placeholder = "Enter dollar amount"
        }
      }
    }

    // Disable/enable Voucher Balance
    if (this.hasVoucherBalanceTarget) {
      this.voucherBalanceTarget.disabled = !isVoucher
      this.voucherBalanceTarget.required = isVoucher
      if (!isVoucher) this.voucherBalanceTarget.value = ""
    }
  }

  updateTieredRulesVisibility() {
    if (!this.hasDiscountTypeTarget) return

    const isTieredPercentage = this.discountTypeTarget.value === TIERED_PERCENTAGE_TYPE
    this.tieredRulesRowTargets.forEach((row) => row.classList.toggle("d-none", !isTieredPercentage))
    this.tieredRuleTargets.forEach((input) => {
      input.disabled = !isTieredPercentage
      input.required = isTieredPercentage
    })
  }

  // "Free Order" applies to two kinds (owner ruling 2026-10-10): a 100%-off
  // percentage coupon and a voucher an admin creates here (a voucher bought in
  // the store never comes through this form, and the model refuses it free).
  //
  // Both default to FREE on create: when the row starts applying (hidden →
  // visible) the box is checked, and switching between a 100% coupon and a
  // voucher keeps the admin's choice. On connect the server already rendered
  // the stored state, so it is left untouched. Whenever the row hides, reset to
  // unchecked so a stale check can't submit free_order=1 for a coupon it does
  // not apply to. The hidden "0" field means every save is an explicit
  // true/false, so a new voucher never stores NULL.
  updateFreeOrderToggle() {
    if (!this.hasFreeOrderRowTarget || !this.hasDiscountTypeTarget || !this.hasDiscountValueTarget) return

    const kind = this.freeOrderKind()
    const becameApplicable = this.freeOrderKindSeen === null && kind !== null
    this.freeOrderKindSeen = kind

    this.freeOrderRowTarget.classList.toggle("d-none", kind === null)
    if (this.hasFreeOrderCouponHintTarget) this.freeOrderCouponHintTarget.classList.toggle("d-none", kind !== "coupon")
    if (this.hasFreeOrderVoucherHintTarget) this.freeOrderVoucherHintTarget.classList.toggle("d-none", kind !== "voucher")

    if (!this.hasFreeOrderTarget) return

    if (kind === null) {
      this.freeOrderTarget.checked = false
    } else if (becameApplicable) {
      this.freeOrderTarget.checked = true
    }
  }

  // "coupon" for a 100%-off percentage coupon, "voucher" for a voucher, null
  // when the Free Order box does not apply.
  freeOrderKind() {
    const type = this.discountTypeTarget.value
    if (type === "voucher") return "voucher"
    if (type === "percentage" && parseFloat(this.discountValueTarget.value) === 100) return "coupon"
    return null
  }

  toggleMaxRedemptions() {
    if (!this.hasLimitTotalRedemptionsTarget || !this.hasMaxRedemptionsFieldTarget) return

    const checked = this.limitTotalRedemptionsTarget.checked
    const select = this.maxRedemptionsFieldTarget.querySelector("select")
    if (select) {
      select.disabled = !checked
      if (!checked) select.value = ""
    }

    if (checked && this.hasMaxTotalRedemptionsTarget && !this.maxTotalRedemptionsTarget.value) {
      this.maxTotalRedemptionsTarget.value = "1"
    }

    // The toggle above may have just changed the select's value/enabled state,
    // so the summary must re-derive AFTER it, not from a parallel action.
    this.updateLimitsSummary()
  }

  markLimitsModified() {
    this.limitsManuallyModified = true
  }

  // New-form default for the two limit checkboxes follows the discount type
  // (owner ruling 2026-09-29, #14875): Fixed Amount and Voucher start
  // unlimited, every other type starts one use per customer capped at 1 total.
  // Stops steering once the admin touches either checkbox, like the end date.
  applyLimitDefaults() {
    if (this.limitsManuallyModified || !this.hasDiscountTypeTarget) return
    if (!this.hasLimitPerCustomerTarget || !this.hasLimitTotalRedemptionsTarget) return

    const limited = !this.unlimitedDefaultTypesValue.includes(this.discountTypeTarget.value)
    this.limitPerCustomerTarget.checked = limited
    this.limitTotalRedemptionsTarget.checked = limited
    // Seeds Max Redemptions at 1 when checked, clears it when not, and
    // re-renders the summary.
    this.toggleMaxRedemptions()
  }

  markDisableRestrictionsModified() {
    this.disableRestrictionsManuallyModified = true
  }

  // New-form default for Disable Restrictions (owner ruling 2026-10-12): ON
  // for a coupon that can apply to Realtime orders, OFF for an aged-only one,
  // where it would do nothing. Stops steering once the admin touches the
  // switch, like the limit defaults.
  applyDisableRestrictionsDefault() {
    if (this.connecting || this.disableRestrictionsManuallyModified || !this.hasDisableRestrictionsTarget) return
    this.disableRestrictionsTarget.checked = this.appliesToRealtime()
  }

  // Mirrors CouponCode#matches_lead_age? and #matches_state? for a Realtime
  // (fresh) order: an empty Bucket or one including "fresh", and no States —
  // any State makes the coupon aged-only.
  appliesToRealtime() {
    const selected = (select) => Array.from(select.selectedOptions).map((o) => o.value).filter(Boolean)
    const ages = this.hasLeadAgesSelectTarget ? selected(this.leadAgesSelectTarget) : []
    const states = this.hasStatesSelectTarget ? selected(this.statesSelectTarget) : []
    return (ages.length === 0 || ages.includes(FRESH_BUCKET)) && states.length === 0
  }

  // Renders the plain-English reading of how the limit toggles compose. The two
  // flags are individually clear but compose ambiguously — "max 3 + one per
  // customer" means "3 different customers, once each", NOT "one customer,
  // 3 uses" — and that exact misreading caused a real support round-trip.
  updateLimitsSummary() {
    if (!this.hasLimitsSummaryTarget) return

    const perCustomer = this.hasLimitPerCustomerTarget && this.limitPerCustomerTarget.checked
    const totalLimited = this.hasLimitTotalRedemptionsTarget && this.limitTotalRedemptionsTarget.checked
    const max = this.hasMaxTotalRedemptionsTarget
      ? parseInt(this.maxTotalRedemptionsTarget.value, 10)
      : NaN

    this.limitsSummaryTarget.textContent = this.limitsSummaryText(perCustomer, totalLimited, max, this.fixedAmountOff())
  }

  // Dollars a Fixed Amount coupon takes off a cart, once (#19387), or null for any other
  // type — the limits summary names it when nothing caps the total (#14875).
  fixedAmountOff() {
    if (!this.hasDiscountTypeTarget || this.discountTypeTarget.value !== FIXED_AMOUNT_TYPE) return null
    if (!this.hasDiscountValueTarget) return null
    return parseFloat(this.discountValueTarget.value)
  }

  // Fixed Amount is a one-order discount, not a credit: point admins who want a
  // balance that carries over to Voucher (#14875).
  // New forms only — retyping a saved coupon is a different decision.
  updateFixedAmountHint() {
    if (!this.hasDiscountTypeTarget) return
    const show = !this.editModeValue && this.discountTypeTarget.value === FIXED_AMOUNT_TYPE
    this.fixedAmountHintTargets.forEach((el) => el.classList.toggle("d-none", !show))
  }

  // The nudge's "yes": flip to Voucher and carry the dollar amount into Voucher
  // Balance. Read the amount FIRST — selecting Voucher zeroes Discount Value.
  // The change event runs every discount-type action (balance field, 365-day
  // end date, limit defaults, this hint), exactly as a hand selection would.
  switchToVoucher() {
    if (!this.hasDiscountTypeTarget) return
    const amount = this.hasDiscountValueTarget ? parseFloat(this.discountValueTarget.value) : NaN

    this.discountTypeTarget.value = "voucher"
    this.discountTypeTarget.dispatchEvent(new Event("change", { bubbles: true }))

    if (!this.hasVoucherBalanceTarget) return
    if (Number.isFinite(amount) && amount > 0) this.voucherBalanceTarget.value = String(amount)
    this.voucherBalanceTarget.focus()
  }

  // KEEP IN SYNC with app/frontend/react/lib/couponLimitsSummary.ts — the React
  // coupon panel shows the same sentence and the two bundles cannot share
  // modules. Both copies are pinned by specs.
  limitsSummaryText(perCustomer, totalLimited, maxRedemptions, fixedAmountOff = null) {
    const max = Number.isFinite(maxRedemptions) && maxRedemptions >= 1 ? Math.floor(maxRedemptions) : null
    const off = Number.isFinite(fixedAmountOff) && fixedAmountOff > 0 ? USD.format(fixedAmountOff) : null

    if (perCustomer && totalLimited) {
      if (max === null) return "Each customer can use this code once — set Max Redemptions to cap how many customers can redeem it."
      if (max === 1) return "Each customer can use this code once — only 1 customer can ever redeem it."
      return `Each customer can use this code once — up to ${max} different customers can redeem it.`
    }
    if (perCustomer) {
      if (off) return `Each customer can use this code once — any number of customers can each take ${off} off.`
      return "Each customer can use this code once — any number of customers can redeem it."
    }
    if (totalLimited) {
      if (max === null) return "Set Max Redemptions to cap how many times this code can be used."
      if (max === 1) return "This code can be redeemed 1 time in total."
      return `This code can be redeemed ${max} times in total — a single customer could use all ${max}.`
    }
    if (off) return `No redemption limits — ${off} off can be applied to any number of orders, by any customer.`
    return "No redemption limits — any customer can use this code any number of times."
  }

  // Locks the Bucket (lead_ages) field to "fresh" for fixed_amount_per_lead
  // coupons without an Aged % Off and frees it otherwise. The model enforces
  // the same rule server-side; this is the matching UI affordance.
  updateLeadAgesLock() {
    if (!this.hasDiscountTypeTarget) return

    const perLead = this.discountTypeTarget.value === PER_LEAD_TYPE
    // The hint and the Aged % Off input only make sense for the per-lead type,
    // so they stay hidden (d-none) / disabled for every other discount type.
    this.leadAgesHintTargets.forEach((el) => el.classList.toggle("d-none", !perLead))
    this.perLeadAgedRowTargets.forEach((el) => el.classList.toggle("d-none", !perLead))
    this.perLeadAgedTargets.forEach((input) => { input.disabled = !perLead })

    if (perLead && !this.perLeadAgedSet()) {
      this.lockLeadAgesToFresh()
    } else {
      // Leaving the per-lead lock for an aged percent: the pinned "fresh" would
      // keep aged orders out, so clear it (the model clears it too).
      this.unlockLeadAges({ clearFreshPin: perLead })
    }
  }

  perLeadAgedSet() {
    return this.perLeadAgedTargets.some((input) => !input.disabled && input.value.trim() !== "")
  }

  // Typing an Aged % Off frees the Bucket at once. Emptying the box re-locks
  // the Bucket only when the admin leaves it (the input's change event runs
  // updateLeadAgesLock), so clearing it to retype a value never throws away
  // the admin's Bucket picks.
  agedPercentInput() {
    if (this.perLeadAgedSet()) this.updateLeadAgesLock()
  }

  lockLeadAgesToFresh() {
    if (!this.hasLeadAgesSelectTarget) return

    const select = this.leadAgesSelectTarget
    Array.from(select.options).forEach((option) => {
      option.selected = option.value === FRESH_BUCKET
    })
    select.disabled = true
    this.notifySelectChanged(select)

    // A disabled <select> submits no value, so inject a hidden field carrying
    // "fresh" — otherwise only the form's empty lead_ages placeholder submits
    // and the coupon would (wrongly) apply to ALL lead ages.
    this.ensureFreshHiddenField()
  }

  unlockLeadAges({ clearFreshPin = false } = {}) {
    const wasLocked = !!this.freshHiddenField
    this.removeFreshHiddenField()
    if (!this.hasLeadAgesSelectTarget) return

    const select = this.leadAgesSelectTarget
    select.disabled = false
    if (clearFreshPin && wasLocked) {
      Array.from(select.options).forEach((option) => { option.selected = false })
    }
    this.notifySelectChanged(select)
  }

  ensureFreshHiddenField() {
    if (this.freshHiddenField) return

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "coupon_code[lead_ages][]"
    input.value = FRESH_BUCKET
    input.dataset.leadAgesFreshLock = "true"
    this.leadAgesSelectTarget.insertAdjacentElement("afterend", input)
    this.freshHiddenField = input
  }

  removeFreshHiddenField() {
    this.freshHiddenField?.remove()
    this.freshHiddenField = null
  }

  // Nudge whatever is rendering this <select> to re-read it after we changed its
  // value or disabled state in JS (neither fires an event on its own).
  //
  // Two audiences, two mechanisms: Select2 listens through jQuery, while the
  // phone summary trigger (mobile_select_summary) listens natively — and
  // jQuery's trigger() does NOT invoke addEventListener listeners. Notify both,
  // or the mobile control keeps showing the pre-lock selection.
  //
  // NOTE for future wiring: the native dispatch below bubbles, so anything later
  // bound to this field via addEventListener or `data-action="change->…"` will
  // ALSO fire on the programmatic lock/unlock, not just on real user input.
  // Nothing is bound today; if you add a handler that must distinguish the two,
  // gate it (e.g. on `event.isTrusted`) rather than removing this dispatch.
  notifySelectChanged(element) {
    const jquery = window.$
    if (typeof jquery === "function") {
      const wrapped = jquery(element)
      if (wrapped && typeof wrapped.trigger === "function") {
        wrapped.trigger("change.select2")
      }
    }

    element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  markEndDateModified() {
    this.endDateManuallyModified = true
  }

  updateEndDateFromStartDate() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget) return
    if (this.endDateManuallyModified) return

    const startDateValue = this.startDateTarget.value
    if (!startDateValue) return

    const startDate = new Date(startDateValue)
    if (isNaN(startDate.getTime())) return

    const durationHours = this.currentDurationHours()
    const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000)
    this.endDateTarget.value = this.formatDateTimeLocal(endDate)

    // Notify browser that the value changed to update validity state
    this.endDateTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  currentDurationHours() {
    const isVoucher = this.hasDiscountTypeTarget && this.discountTypeTarget.value === "voucher"
    return isVoucher ? this.voucherDurationHoursValue : this.defaultDurationHoursValue
  }

  formatDateTimeLocal(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    const hours = String(date.getHours()).padStart(2, "0")
    const minutes = String(date.getMinutes()).padStart(2, "0")

    return `${year}-${month}-${day}T${hours}:${minutes}`
  }
}
